/**
 * One candidate in a Race: the name (linking to their Person view with
 * Election/Race return context) and Party pill, then a row of pills —
 * incumbency and ballot standing at the start, the Response status and
 * endorsement at the end.
 */
import type {BallotCandidate} from "../ballot-model";
import type { PublicRaceCandidate } from "../api";
import { h } from "../dom";
import {
  candidacyStatusLabel,
  candidacyStatusValue,
  endorsedBadge,
  incumbentBadge,
  partyBadge,
  partyName,
  responseBadge,
  revokedBadge,
} from "../labels";
import { personHref } from "../links";

export interface RowContext {
  page: URL;
  cycle: string;
  race: string;
  stage: string;
}

export function candidateRow(c: PublicRaceCandidate, ctx: RowContext): HTMLElement {
  const href = personHref(ctx.page, c.publicUrl, { from: ctx.cycle, race: ctx.race });
  const party = partyName(c.party);
  const cycleRow = "key" in c ? c as BallotCandidate : null;
  const status = candidacyStatusLabel(c.status, cycleRow?.stage ?? ctx.stage);
  // Winning the Race itself (not a primary) is the one filled green result.
  const finalWin = candidacyStatusValue(c.status) === "won" && (cycleRow?.stage ?? ctx.stage) !== "primary";
  const endorsement = c.endorsement.endorsed ? endorsedBadge() : c.endorsement.revokedInBallot ? revokedBadge() : null;
  if (endorsement && cycleRow?.endorsementBallotLabel) {
    endorsement.append(document.createTextNode(" "), h("span", {class:"bsc-badge__ballot"}, cycleRow.endorsementBallotLabel));
  }
  // The pill lives inside the link so the whole row, pill included, is one
  // link; the name link's ::after stretches it across the row.
  const nameParts = [
    party ? partyBadge(party) : null,
    h("span", { class: "bsc-candidate__name-text" }, c.name),
    party ? h("span", { class: "bsc-sr-only" }, ", ", h("span", { class: "bsc-candidate__party" }, party)) : null,
  ];
  const name = href
    ? h("a", { class: "bsc-candidate__name", href }, nameParts,
        h("span", { class: "bsc-candidate__arrow", "aria-hidden": "true" }, "→"))
    : h("span", { class: "bsc-candidate__name" }, nameParts);
  const standing = [
    c.isIncumbent ? incumbentBadge() : null,
    status && cycleRow?.showStatusBadge !== false ? h("span", { class: `bsc-badge bsc-badge--candidacy${finalWin ? " bsc-badge--won" : ""}` }, status) : null,
    ...(cycleRow?.tags.map(tag => h("span", { class: "bsc-stage-tag", "data-stage-status": tag.status }, tag.text)) ?? []),
  ].filter(Boolean);
  return h(
    "li",
    { class: "bsc-candidate", id: cycleRow ? `bsc-candidate-${cycleRow.key}` : null, "data-status": candidacyStatusValue(c.status) },
    h(
      "div",
      { class: "bsc-candidate__main" },
      h("h4", { class: "bsc-candidate__heading" }, name),
      h(
        "div",
        { class: "bsc-candidate__badges" },
        standing.length ? h("div", { class: "bsc-candidate__standing" }, standing) : null,
        h("div", { class: "bsc-candidate__review" }, c.responseStatus ? responseBadge(c.responseStatus) : null, endorsement)
      )
    )
  );
}
